import React, { useState, useEffect, useContext } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { ListFilter, Edit, Trash2, ArrowUp, ArrowDown, Plus } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import WorkPermitCreation from "./WPCreateOrder";
import { UserContext } from "../../contexts/UserContext";

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, "")}/api`;

// AutoPlanningModal Component
const AutoPlanningModal = ({ isOpen, onClose, selectedOrders, teams, users = [], onConfirmPlanning }) => {
  // Helper to get today's date as YYYY-MM-DD string
  const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [dateRange, setDateRange] = useState({
    startDate: getTodayString(),
    endDate: getTodayString(),
  });
  const [settings, setSettings] = useState({
    ordersPerTeam: 1,
    selectedTeams: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [teamSearchQuery, setTeamSearchQuery] = useState("");

  const today = getTodayString();

  // Helper to get user name by ID
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}`.trim() : null;
  };

  // Helper to get team members with roles
  const getTeamMembers = (team) => {
    const members = [];

    // Team Leader (Supervisor)
    if (team.team_leader) {
      const name = getUserName(team.team_leader);
      if (name) members.push({ name, role: 'Supervisor' });
    }

    // Work Permit Preparer
    if (team.person_responsible_for_preparation_of_work_place) {
      const name = getUserName(team.person_responsible_for_preparation_of_work_place);
      if (name && team.person_responsible_for_preparation_of_work_place !== team.team_leader) {
        members.push({ name, role: 'Preparer' });
      }
    }

    // Regular team members
    if (team.team && Array.isArray(team.team)) {
      team.team.forEach(memberId => {
        // Skip if already added as leader or preparer
        if (memberId === team.team_leader || memberId === team.person_responsible_for_preparation_of_work_place) {
          return;
        }
        const name = getUserName(memberId);
        if (name) members.push({ name, role: 'Member' });
      });
    }

    return members;
  };

  // Filter teams by search query (team name or member names)
  const filteredTeams = teams.filter((team) => {
    if (!teamSearchQuery.trim()) return true;

    const searchLower = teamSearchQuery.toLowerCase().trim();

    // Check team name
    if (team.name.toLowerCase().includes(searchLower)) return true;

    // Check all team members (including leader and preparer)
    const members = getTeamMembers(team);
    return members.some(member =>
      member.name.toLowerCase().includes(searchLower)
    );
  });

  const handleStartDateChange = (newStartDate) => {
    setDateRange(prev => ({
      startDate: newStartDate,
      // If end date is before new start date, update it to match start date
      endDate: prev.endDate < newStartDate ? newStartDate : prev.endDate
    }));
  };

  const handleTeamSelection = (teamId) => {
    setSettings((prev) => ({
      ...prev,
      selectedTeams: prev.selectedTeams.includes(teamId)
        ? prev.selectedTeams.filter((id) => id !== teamId)
        : [...prev.selectedTeams, teamId],
    }));
  };

  const handlePlanningConfirm = () => {
    const planningData = {
      dateRange,
      settings,
      selectedOrders,
    };
    onConfirmPlanning(planningData);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] h-[95vh] flex flex-col rounded-xl">
        <DialogHeader className="flex-shrink-0 bg-gradient-to-r from-brand-50 to-white -mx-6 -mt-6 px-6 py-4 rounded-t-xl border-b border-brand-100">
          <DialogTitle className="text-lg font-bold text-slate-900">Auto Planning Settings</DialogTitle>
          <div className="text-sm text-slate-500">
            Selected orders: {selectedOrders.length}
          </div>
        </DialogHeader>
        <div className="flex-1 flex flex-col min-h-0 space-y-4 pt-4">
          {/* Planning Period - fixed height */}
          <div className="flex-shrink-0 space-y-4">
            <h3 className="text-xs font-bold text-slate-500 uppercase">Planning Period</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Start Date</label>
                <input
                  type="date"
                  className="w-full p-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  value={dateRange.startDate}
                  min={today}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">End Date</label>
                <input
                  type="date"
                  className="w-full p-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  value={dateRange.endDate}
                  min={dateRange.startDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
            </div>
          </div>
          {/* Team Settings - flexible height with scrollable teams list */}
          <div className="flex-1 flex flex-col min-h-0 space-y-4">
            <h3 className="text-xs font-bold text-slate-500 uppercase flex-shrink-0">Team Settings</h3>
            <div className="flex-shrink-0">
              <label className="mr-4 text-sm text-slate-600">Orders per Team per Day ( max 5 )</label>
              <input
                type="number"
                min="1"
                max="5"
                className="w-14 p-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                value={settings.ordersPerTeam}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    ordersPerTeam: Math.max(1, Math.min(5, parseInt(e.target.value) || 1)),
                  }))
                }
              />
            </div>
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex items-center justify-between flex-shrink-0">
                <label className="text-sm text-slate-600">Select Teams</label>
                <input
                  type="text"
                  placeholder="Search teams..."
                  value={teamSearchQuery}
                  onChange={(e) => setTeamSearchQuery(e.target.value)}
                  className="w-40 px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div className="mt-2 space-y-2 flex-1 overflow-y-auto min-h-0">
                {filteredTeams.map((team) => {
                  const members = getTeamMembers(team);
                  const supervisor = members.find(m => m.role === 'Supervisor');
                  const preparer = members.find(m => m.role === 'Preparer');
                  const regularMembers = members.filter(m => m.role === 'Member');

                  return (
                    <label
                      key={team.id}
                      className="flex items-start p-3 border border-slate-200 rounded-lg hover:bg-brand-50 text-sm cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={settings.selectedTeams.includes(team.id)}
                        onChange={() => handleTeamSelection(team.id)}
                        className="mr-3 mt-1 text-brand-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">{team.name}</div>
                        {members.length > 0 ? (
                          <div className="mt-1 text-[11px] text-slate-600 space-y-0.5">
                            {(supervisor || preparer) && (
                              <div>
                                {supervisor && (
                                  <span>
                                    <span className="text-slate-400">Supervisor:</span>{' '}
                                    <span className="text-slate-700">{supervisor.name}</span>
                                  </span>
                                )}
                                {supervisor && preparer && (
                                  <span className="text-slate-300 mx-1.5">|</span>
                                )}
                                {preparer && (
                                  <span>
                                    <span className="text-slate-400">Preparer:</span>{' '}
                                    <span className="text-slate-700">{preparer.name}</span>
                                  </span>
                                )}
                              </div>
                            )}
                            {regularMembers.length > 0 && (
                              <div>
                                <span className="text-slate-400">Members:</span>{' '}
                                <span className="text-slate-700">
                                  {regularMembers.map(m => m.name).join(', ')}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-[11px] text-slate-400 mt-1">No members assigned</div>
                        )}
                      </div>
                    </label>
                  );
                })}
                {filteredTeams.length === 0 && (
                  <div className="text-center text-slate-400 text-sm py-4">
                    {teamSearchQuery ? `No teams found matching "${teamSearchQuery}"` : 'No teams available'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {error && <div className="text-red-600 text-sm mt-2 flex-shrink-0">{error}</div>}
        <DialogFooter className="flex-shrink-0 border-t border-slate-200 -mx-6 -mb-6 px-6 py-4 bg-slate-50 rounded-b-xl">
          <Button variant="outline" onClick={onClose} disabled={loading} className="px-4 py-2 bg-white text-slate-700 rounded-lg hover:bg-slate-100 border border-slate-300">
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handlePlanningConfirm}
            disabled={loading || settings.selectedTeams.length === 0 || dateRange.startDate > dateRange.endDate}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 shadow-lg shadow-brand-500/20 disabled:opacity-50"
          >
            {loading ? "Planning..." : "Confirm Planning"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// PreOrderList Component
const PreOrderList = ({
  isOpen,
  onClose,
  teams = [],
  users = [],
  onOrdersPlanned = () => {},
}) => {
  const { user } = useContext(UserContext);
  const [workPermits, setWorkPermits] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "order_number",
    direction: "asc",
  });
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [showPlanningModal, setShowPlanningModal] = useState(false);
  const [showWorkPermitCreation, setShowWorkPermitCreation] = useState(false);
  const [editOrderData, setEditOrderData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Pobieranie workPermits ze statusem pending
  useEffect(() => {
    const fetchWorkPermits = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("access_token");
        if (!token) {
          toast.error("No access token found. Please log in again.");
          return;
        }

        const config = {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: "pending" },
        };

        const response = await axios.get(`${API_BASE_URL}/workpermit/`, config);
        setWorkPermits(response.data || []);
      } catch (error) {
        console.error("Error fetching work permits:", error);
        toast.error("Failed to load orders. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchWorkPermits();
    }
  }, [isOpen]);

  const refreshPreOrders = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        toast.error("No access token found. Please log in again.");
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: "pending" },
      };

      const response = await axios.get(`${API_BASE_URL}/workpermit/`, config);
      setWorkPermits(response.data || []);
    } catch (error) {
      console.error("Error refreshing pre-orders:", error);
      toast.error("Failed to refresh orders list.");
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const filteredOrderIds = sortedAndFilteredOrders
        .filter((order) => order?.id)
        .map((order) => order.id);
      setSelectedOrders(new Set(filteredOrderIds));
    } else {
      setSelectedOrders(new Set());
    }
  };

  const handleSelectOrder = (orderId) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="h-4 w-4 ml-1" strokeWidth={1.5} />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1" strokeWidth={1.5} />
    );
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) {
      return;
    }

    const token = localStorage.getItem("access_token");
    if (!token) {
      toast.error("No access token found. Please log in again.");
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/workpermit/${orderId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await refreshPreOrders();
      setSelectedOrders((prev) => {
        const newSelected = new Set(prev);
        newSelected.delete(orderId);
        return newSelected;
      });
      toast.success(`Order ${orderId} deleted successfully`);
    } catch (error) {
      console.error("Error deleting order:", error);
      if (error.response?.status === 401) {
        toast.error("Unauthorized. Please log in again.");
      } else {
        toast.error("Failed to delete order. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewOrder = () => {
    setEditOrderData(null);
    setShowWorkPermitCreation(true);
  };

  const handleEditClick = (order) => {
    setEditOrderData({
      id: order.id,
      order_number: order.order_number,
      work_permit_number: order.work_permit_number,
      turbine: order.turbine,
      work_scope: order.work_scope,
      company_to: order.company_to,
      work_scope_real: order.work_scope_real,
      comment: order.comment,
      team: order.team,
      planned_date_start: order.planned_date_start ? new Date(order.planned_date_start) : null,
      planned_date_end: order.planned_date_end ? new Date(order.planned_date_end) : null,
    });
    setShowWorkPermitCreation(true);
  };

  const handleWorkPermitCreationClose = () => {
    setShowWorkPermitCreation(false);
    setEditOrderData(null);
  };

  const handlePlanningConfirm = async (planningData) => {
    try {
      const token = localStorage.getItem("access_token");
      const { dateRange, settings } = planningData;

      const selectedOrderDetails = workPermits.filter((order) =>
        selectedOrders.has(order.id)
      );

      let successfulUpdates = 0;
      const totalOrders = selectedOrderDetails.length;

      // Generate array of dates from start to end (inclusive)
      const dates = [];
      let currentDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);

      // Normalize both dates to midnight for accurate comparison
      currentDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      // Include end date by using <= comparison
      while (currentDate.getTime() <= endDate.getTime()) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      let currentTeamIndex = 0;
      let currentDateIndex = 0;

      const getWeekNumber = (date) => {
        const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
        const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
        return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay()) / 7);
      };

      for (const order of selectedOrderDetails) {
        try {
          const currentOrder = await axios.get(
            `${API_BASE_URL}/workpermit/${order.id}/`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const teamId = settings.selectedTeams[currentTeamIndex];
          const plannedDate = dates[currentDateIndex];
          const year = plannedDate.getFullYear();
          const weekNumber = getWeekNumber(plannedDate);

          const teamResponse = await axios.get(
            `${API_BASE_URL}/teams/${teamId}/`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const team = teamResponse.data;

          const activePeriods = { ...team.active_periods };
          if (!activePeriods[year]) {
            activePeriods[year] = [];
          }
          if (!activePeriods[year].includes(weekNumber)) {
            activePeriods[year].push(weekNumber);
            activePeriods[year].sort((a, b) => a - b);

            await axios.put(
              `${API_BASE_URL}/teams/${teamId}/`,
              {
                ...team,
                active_periods: activePeriods,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
            toast.info(`Team ${team.name} updated with week ${weekNumber} for ${year}`);
          }

          const startDate = new Date(plannedDate);
          startDate.setHours(8, 0, 0);
          const endDate = new Date(plannedDate);
          endDate.setHours(16, 0, 0);

          const updateData = {
            ...currentOrder.data,
            team: teamId,
            status: "planned",
            planned_date_start: startDate.toISOString(),
            planned_date_end: endDate.toISOString(),
          };

          const response = await axios.put(
            `${API_BASE_URL}/workpermit/${order.id}/`,
            updateData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (response.status === 200) {
            successfulUpdates++;
          }

          currentTeamIndex = (currentTeamIndex + 1) % settings.selectedTeams.length;
          if (currentTeamIndex === 0) {
            currentDateIndex = (currentDateIndex + 1) % dates.length;
          }
        } catch (error) {
          console.error(`Error updating order ${order.id}:`, error);
        }
      }

      await refreshPreOrders();
      onClose();
      onOrdersPlanned();

      if (successfulUpdates === totalOrders) {
        toast.success(`Successfully planned all ${successfulUpdates} orders.`);
      } else if (successfulUpdates > 0) {
        toast.info(`Planned ${successfulUpdates} out of ${totalOrders} orders.`);
      } else {
        toast.error("Failed to plan any orders.");
      }
    } catch (error) {
      console.error("Error during planning:", error);
      toast.error("An error occurred during planning.");
    }
  };

  const getSortedAndFilteredOrders = () => {
    let filteredOrders = (workPermits || []).filter((order) => {
      const searchFields = [
        order.order_number,
        order.work_permit_number,
        order.turbine_data?.location,
        order.turbine_data?.turbine_number,
        order.turbine_data?.wind_farm_data?.name,
        order.work_scope_data?.name,
        order.work_scope_real,
        order.turbine_data?.model_data?.name,
      ].map((field) => String(field || "").toLowerCase());
      return searchFields.some((field) => field.includes(searchTerm.toLowerCase()));
    });

    return filteredOrders.sort((a, b) => {
      if (!a || !b) return 0;
      let aValue =
        sortConfig.key === "windfarm"
          ? a.turbine_data?.wind_farm_data?.name
          : sortConfig.key === "location"
            ? a.turbine_data?.location
            : sortConfig.key === "turbine_number"
              ? a.turbine_data?.turbine_number
              : sortConfig.key === "name"
                ? a.work_scope_data?.name
                : a[sortConfig.key] || "";
      let bValue =
        sortConfig.key === "windfarm"
          ? b.turbine_data?.wind_farm_data?.name
          : sortConfig.key === "location"
            ? b.turbine_data?.location
            : sortConfig.key === "turbine_number"
              ? b.turbine_data?.turbine_number
              : sortConfig.key === "name"
                ? b.work_scope_data?.name
                : b[sortConfig.key] || "";
      aValue = String(aValue || "").toLowerCase();
      bValue = String(bValue || "").toLowerCase();
      return sortConfig.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  };

  const sortedAndFilteredOrders = getSortedAndFilteredOrders();

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-8xl h-[95vh] flex flex-col rounded-xl">
          <DialogHeader className="flex-shrink-0 bg-gradient-to-r from-brand-50 to-white -mx-6 -mt-6 px-6 py-4 rounded-t-xl border-b border-brand-100">
            <div className="flex justify-between items-center mb-4">
              <DialogTitle className="text-xl font-bold text-slate-900">
                Orders List
              </DialogTitle>
              <Button
                onClick={handleAddNewOrder}
                className="mr-8 bg-brand-500 text-white px-4 py-2 rounded-lg hover:bg-brand-600 transition-colors duration-300 flex items-center shadow-lg shadow-brand-500/20"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Order
              </Button>
            </div>
            <div className="flex items-center">
              <div className="relative flex-1">
                <ListFilter className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by order number, work permit number, location, turbine, windfarm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 px-3 py-2 text-sm"
                  disabled={loading}
                />
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-hidden mt-4">
            <div className="h-full overflow-x-auto">
              <div className="h-full max-h-[calc(90vh-200px)] overflow-y-auto scrollbar-gutter-stable">
                <div className="min-w-[1200px] grid grid-cols-[40px,160px,240px,2fr,1fr,1fr,2fr,120px] w-full">
                  <div className="grid-table-header contents">
                    <div className="sticky top-0 bg-steel-100 z-20 border-b-2 border-brand-500 p-2">
                      <input
                        type="checkbox"
                        checked={selectedOrders.size === sortedAndFilteredOrders.length && sortedAndFilteredOrders.length > 0}
                        name="handleSelectAll"
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="rounded border-slate-300 text-brand-500"
                      />
                    </div>
                    <div
                      className="sticky top-0 bg-steel-100 z-20 cursor-pointer border-b-2 border-brand-500"
                      onClick={() => handleSort("order_number")}
                    >
                      <div className="flex items-center gap-2 p-2 text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Order Number
                        {renderSortIndicator("order_number")}
                      </div>
                    </div>
                    <div
                      className="sticky top-0 bg-steel-100 z-20 cursor-pointer border-b-2 border-brand-500"
                      onClick={() => handleSort("work_permit_number")}
                    >
                      <div className="flex items-center gap-2 p-2 text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Work Permit Number
                        {renderSortIndicator("work_permit_number")}
                      </div>
                    </div>
                    <div
                      className="sticky top-0 bg-steel-100 z-20 cursor-pointer border-b-2 border-brand-500"
                      onClick={() => handleSort("location")}
                    >
                      <div className="flex items-center gap-2 p-2 text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Location
                        {renderSortIndicator("location")}
                      </div>
                    </div>
                    <div
                      className="sticky top-0 bg-steel-100 z-20 cursor-pointer border-b-2 border-brand-500"
                      onClick={() => handleSort("turbine_number")}
                    >
                      <div className="flex items-center gap-2 p-2 text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Turbine
                        {renderSortIndicator("turbine_number")}
                      </div>
                    </div>
                    <div
                      className="sticky top-0 bg-steel-100 z-20 cursor-pointer border-b-2 border-brand-500"
                      onClick={() => handleSort("windfarm")}
                    >
                      <div className="flex items-center gap-2 p-2 text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Windfarm
                        {renderSortIndicator("windfarm")}
                      </div>
                    </div>
                    <div
                      className="sticky top-0 bg-steel-100 z-20 cursor-pointer border-b-2 border-brand-500"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center gap-2 p-2 text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Work Scope
                        {renderSortIndicator("name")}
                      </div>
                    </div>
                    <div className="sticky top-0 bg-steel-100 z-20 text-right border-b-2 border-brand-500">
                      <div className="p-2 text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</div>
                    </div>
                  </div>
                  {loading ? (
                    <div className="grid-table-row contents">
                      <div className="col-span-8 text-center py-8 bg-white text-slate-600">
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500"></div>
                          Loading...
                        </div>
                      </div>
                    </div>
                  ) : sortedAndFilteredOrders.length > 0 ? (
                    sortedAndFilteredOrders.map((order, index) => {
                      const duplicateDetails = workPermits.filter(
                        (o) => o.work_permit_number === order.work_permit_number
                      );
                      const isDuplicate = duplicateDetails.length > 1;
                      return (
                        <div
                          key={order?.id || index}
                          className={`grid-table-row contents hover:bg-brand-50 cursor-pointer transition-colors ${isDuplicate ? "bg-red-50" : ""}`}
                          onDoubleClick={() => handleEditClick(order)}
                        >
                          <div className="border-b border-steel-200 p-2">
                            <input
                              type="checkbox"
                              checked={selectedOrders.has(order.id)}
                              onChange={() => handleSelectOrder(order.id)}
                              className="rounded border-slate-300 text-brand-500"
                            />
                          </div>
                          <div className="border-b border-steel-200">
                            <div className="flex items-center p-2">
                              <div className="text-sm font-medium text-slate-900 truncate">
                                {order?.order_number || "-"}
                                {isDuplicate && (
                                  <span className="text-red-600 text-[10px] ml-1">
                                    (Duplicate)
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="border-b border-steel-200">
                            <div className="flex items-center p-2">
                              <div className="text-sm font-medium text-slate-900 truncate">
                                {order?.work_permit_number || "-"}
                              </div>
                            </div>
                          </div>
                          <div className="border-b border-steel-200">
                            <div className="text-sm text-slate-600 p-2">
                              {order?.turbine_data?.location || "-"}
                            </div>
                          </div>
                          <div className="border-b border-steel-200">
                            <div className="text-sm text-slate-600 p-2">
                              {order?.turbine_data?.turbine_number || "-"} /{" "}
                              {order?.turbine_data?.model_data?.name || "-"}
                            </div>
                          </div>
                          <div className="border-b border-steel-200">
                            <div className="text-sm text-slate-600 p-2">
                              {order?.turbine_data?.wind_farm_data?.name || "-"}
                            </div>
                          </div>
                          <div className="border-b border-steel-200">
                            <div className="text-sm text-slate-600 p-2">
                              {order.work_scope_data?.name === "Other"
                                ? `${order.work_scope_real} (undefined)`
                                : order?.work_scope_data?.name || "-"}
                            </div>
                          </div>
                          <div className="text-right border-b border-steel-200">
                            <div className="flex justify-end space-x-2 p-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(order);
                                }}
                                className="p-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                                title="Edit Order"
                                disabled={loading}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(order.id);
                                }}
                                className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                                title="Delete Order"
                                disabled={loading}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="grid-table-row contents">
                      <div className="col-span-8 text-center py-8 bg-white text-slate-500">
                        No orders found.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 border-t border-slate-200 -mx-6 -mb-6 px-6 py-4 bg-slate-50 rounded-b-xl">
            <div className="flex justify-between items-center w-full">
              <div className="ml-4 text-sm text-slate-600">
                {selectedOrders.size} orders selected
              </div>
              <div className="mr-4 space-x-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="px-4 py-2 bg-white text-slate-700 rounded-lg hover:bg-slate-100 border border-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  disabled={selectedOrders.size === 0}
                  onClick={() => setShowPlanningModal(true)}
                  variant="outline"
                  className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 shadow-lg shadow-brand-500/20 disabled:opacity-50"
                >
                  Plan Selected Orders
                </Button>
              </div>
            </div>
          </DialogFooter>

          <AutoPlanningModal
            isOpen={showPlanningModal}
            onClose={() => setShowPlanningModal(false)}
            selectedOrders={Array.from(selectedOrders)}
            teams={teams}
            users={users}
            onConfirmPlanning={handlePlanningConfirm}
          />
        </DialogContent>
      </Dialog>

      <WorkPermitCreation
        isOpen={showWorkPermitCreation}
        onClose={handleWorkPermitCreationClose}
        refreshPreOrders={refreshPreOrders}
        team={null}
        data={editOrderData}
      />
    </>
  );
};

export default PreOrderList;